<template>
  <div
    v-if="isLoadingSymbol"
    class="p-4 w-96 h-full flex items-center justify-center"
  >
    <NLoading class="text-brand-primary" />
  </div>

  <div v-else class="bg-neutral-secondary p-4 w-96 h-full rounded-md">
    <NBox>
      <div class="flex justify-between items-baseline py-2 px-3">
        <div class="flex flex-col">
          <div class="inline-flex items-center">
            <SymbolTradeStateIcon
              class="me-2"
              :trading-state-code="symbol.instrumentTradingState"
            />

            <Retail v-if="getSymbolIsin(symbol)" :isin="getSymbolIsin(symbol)">
              <NText variant="h3" tag="h3" weight="bold">
                {{ symbol.instrumentName }}
              </NText>
            </Retail>
          </div>

          <NText variant="caption1" class="text-neutral-secondary">
            {{ symbol.instrumentTitle }}
          </NText>
        </div>

        <div class="flex flex-col justify-around">
          <div class="inline-flex items-center">
            <NumberFormatter
              #="{ number }"
              :value="(symbol.referencePriceVariationPercentage ?? 0) / 100"
              maximum-fraction-digits="2"
              sign-display="always"
              number-style="percent"
            >
              <NText
                weight="bold"
                dir="ltr"
                class="mx-1"
                :class="{
                  'text-brand-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) > 0,
                  'text-critical-primary':
                    (symbol.referencePriceVariationPercentage ?? 0) < 0,
                }"
              >
                ({{ number }})
              </NText>
            </NumberFormatter>

            <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
              <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
                <NText :key="number" variant="h1" tag="h1" weight="bold">
                  {{ number }}
                </NText>
              </ChangePriceEffect>
            </NumberFormatter>
          </div>

          <NText
            variant="overline"
            weight="bold"
            class="self-end text-neutral-tertiary-disable"
          >
            {{ t('misc.rial') }}
          </NText>
        </div>
      </div>

      <div
        class="flex justify-between items-center py-2 px-3 border-y border-neutral-secondary"
      >
        <NText variant="caption1">
          {{ t('modules.order.closingPrice') }}
        </NText>

        <span>
          <NumberFormatter
            #="{ number }"
            :value="(symbol.closingPriceVariationPercent ?? 0) / 100"
            maximum-fraction-digits="2"
            sign-display="always"
            number-style="percent"
          >
            <NText
              variant="caption1"
              weight="bold"
              dir="ltr"
              class="mx-1"
              :class="{
                'text-brand-primary':
                  (symbol.closingPriceVariationPercent ?? 0) > 0,
                'text-critical-primary':
                  (symbol.closingPriceVariationPercent ?? 0) < 0,
              }"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="symbol.closingPrice">
            <NText variant="caption1" weight="bold">
              {{ number }}
            </NText>
          </NumberFormatter>
        </span>
      </div>

      <div class="flex justify-between items-center py-2 px-3">
        <NText variant="caption1">{{ t('symbol.trade_volume') }}</NText>

        <NumberFormatter
          #="{ number }"
          :value="symbol.totalNumberOfSharesTraded"
          maximum-fraction-digits="2"
          notation="compact"
        >
          <NText variant="caption1" weight="bold">
            {{ number }}
          </NText>
        </NumberFormatter>
      </div>
    </NBox>

    <SymbolBidAskTable
      v-if="symbol.bidAsk"
      :items="symbol.bidAsk"
      :isin="symbol.isin ?? ''"
      :lower-price-threshold="symbol.lowerPriceThreshold ?? 0"
      :upper-price-threshold="symbol.upperPriceThreshold ?? 0"
      size="large"
      class="my-4"
      :update-order-form-data="true"
      show-unit
      @update-order="
        ({ quantity, price }) => emit('updateOrderForm', { quantity, price })
      "
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { getSymbolIsin } from '@core/symbol';
import { useI18n } from 'vue-i18n';

import {
  SymbolBidAskTable,
  Retail,
  useLiveSymbolQuery,
  SymbolTradeStateIcon,
} from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

type OrderFormDetailProps = {
  isin: string;
};

type OrderFormDetailEmits = {
  (
    e: 'updateOrderForm',
    { quantity, price }: { quantity?: number; price?: number }
  ): void;
};

const props = defineProps<OrderFormDetailProps>();
const emit = defineEmits<OrderFormDetailEmits>();

const { t } = useI18n();

const isinRef = ref(props.isin);

const { symbol, isLoadingSymbol } = useLiveSymbolQuery(isinRef);
</script>
